import { useEffect, useMemo, useState } from 'react';
import { FolderInput, Loader2, Search, X, ChevronLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Task, tasksApi } from '../api/tasks';
import { projectsApi, Project } from '../api/projects';

/**
 * Deliver tasks into another project, in two steps.
 *
 * Step 1 picks the tasks. Everything the caller passed starts SELECTED, because
 * the common case is "hand this project's work over" — the picker exists to drop
 * the few that should stay behind, not to build the list from nothing.
 * Step 2 picks the destination project.
 *
 * Shared by the project page (which names the project being delivered out of)
 * and the global task list (which has no source — there the server hands over
 * each task's own primary project link).
 */

interface DeliverTasksDialogProps {
  open: boolean;
  onClose: () => void;
  /** Candidate tasks — typically whatever the calling page is showing. */
  tasks: Task[];
  /** The project being delivered OUT of, when there is one. */
  fromProjectId?: string;
  /** Shown in the summary line, e.g. the source project's name. */
  fromLabel?: string;
  /** Called after at least one task moved, so the page can reload. */
  onDelivered: () => void;
}

export default function DeliverTasksDialog({
  open,
  onClose,
  tasks,
  fromProjectId,
  fromLabel,
  onDelivered,
}: DeliverTasksDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [taskSearch, setTaskSearch] = useState('');

  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [targetId, setTargetId] = useState('');
  const [note, setNote] = useState('');
  const [delivering, setDelivering] = useState(false);

  // Reset every time the dialog is opened: a stale selection from the previous
  // run would silently deliver tasks the user never looked at this time.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedIds(new Set(tasks.map(t => t.id)));
    setTaskSearch('');
    setProjectSearch('');
    setTargetId('');
    setNote('');
    setDelivering(false);
    // `tasks` is intentionally read only at open time — re-selecting everything
    // because the parent re-rendered would undo the user's deselections.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visibleTasks = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(t =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.code || '').toLowerCase().includes(q) ||
      (t.primary_assignee_name || '').toLowerCase().includes(q) ||
      (t.status || '').toLowerCase().includes(q)
    );
  }, [tasks, taskSearch]);

  const visibleProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.code || '').toLowerCase().includes(q)
    );
  }, [projects, projectSearch]);

  // Select-all acts on what the SEARCH is showing, so it stays honest while
  // filtered: ticking it with "design" typed selects those, not all 200.
  const visibleIds = visibleTasks.map(t => t.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every(tid => selectedIds.has(tid));

  const toggleOne = (taskId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach(tid => next.delete(tid));
      else visibleIds.forEach(tid => next.add(tid));
      return next;
    });
  };

  const goToProjects = async () => {
    setStep(2);
    if (projects.length > 0) return;
    setLoadingProjects(true);
    try {
      // Archived projects are left out: delivering work into one would hide it
      // the moment it arrived.
      const res = await projectsApi.list({ page_size: 200 });
      const items: Project[] = res.data?.items || [];
      setProjects(
        items.filter(p => p.id !== fromProjectId && p.status !== 'ARCHIVED'),
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not load projects');
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const doDeliver = async () => {
    if (!targetId || selectedIds.size === 0) return;
    setDelivering(true);
    try {
      const res = await tasksApi.bulkMoveProject({
        task_ids: Array.from(selectedIds),
        to_project_id: targetId,
        from_project_id: fromProjectId,
        note: note.trim() || undefined,
      });
      const { moved_count, skipped_count, skipped, to_project } = res.data;
      if (moved_count > 0) {
        toast.success(
          `${moved_count} task${moved_count === 1 ? '' : 's'} delivered to ${to_project.name}`,
        );
      }
      // Skips are per-task and each carries a reason (no permission, already
      // there) — reporting only the successes would quietly lose them.
      if (skipped_count > 0) {
        const first = skipped[0];
        toast.warning(
          `${skipped_count} task${skipped_count === 1 ? '' : 's'} not delivered`,
          { description: first ? `${first.title || first.task_id}: ${first.reason}` : undefined },
        );
      }
      onClose();
      if (moved_count > 0) onDelivered();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not deliver tasks');
    } finally {
      setDelivering(false);
    }
  };

  if (!open) return null;

  const targetProject = projects.find(p => p.id === targetId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      style={{ background: 'rgba(15, 29, 20, 0.45)', backdropFilter: 'blur(6px)' }}
      onClick={() => !delivering && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-6 flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="gt-leaf" />
            <h3 className="gt-display" style={{ fontSize: 20, color: 'var(--gt-text-primary)' }}>
              {step === 1 ? 'Select tasks to deliver' : 'Deliver to which project?'}
            </h3>
            <span className="gt-page-sub gt-numerals" style={{ fontSize: 12 }}>
              Step {step} of 2
            </span>
          </div>
          <button
            onClick={onClose}
            disabled={delivering}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ---------------- STEP 1 — pick the tasks ---------------- */}
        {step === 1 && (
          <>
            <div className="px-6 pt-4 pb-2 space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={taskSearch}
                  onChange={e => setTaskSearch(e.target.value)}
                  placeholder="Search tasks by title, code, assignee or status…"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    disabled={visibleIds.length === 0}
                    className="w-4 h-4 cursor-pointer"
                  />
                  {allVisibleSelected ? 'Clear all' : 'Select all'}
                  {taskSearch.trim() ? ` (${visibleIds.length} shown)` : ` (${tasks.length})`}
                </label>
                <span className="gt-page-sub gt-numerals" style={{ fontSize: 13 }}>
                  {selectedIds.size} of {tasks.length} selected
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-2 min-h-[160px]">
              {tasks.length === 0 ? (
                <p className="gt-page-sub text-center py-10" style={{ fontSize: 13 }}>
                  There are no tasks here to deliver.
                </p>
              ) : visibleTasks.length === 0 ? (
                <p className="gt-page-sub text-center py-10" style={{ fontSize: 13 }}>
                  No task matches “{taskSearch}”.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {visibleTasks.map(t => {
                    const checked = selectedIds.has(t.id);
                    return (
                      <li key={t.id}>
                        <label className="flex items-start gap-3 py-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(t.id)}
                            className="w-4 h-4 mt-0.5 cursor-pointer flex-shrink-0"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2 flex-wrap">
                              {t.code && (
                                <span
                                  className="gt-numerals px-1.5 py-0.5 rounded text-[11px] font-semibold"
                                  style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}
                                >
                                  {t.code}
                                </span>
                              )}
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {t.title}
                              </span>
                            </span>
                            <span className="gt-page-sub block mt-0.5" style={{ fontSize: 12 }}>
                              {t.status}
                              {t.primary_assignee_name ? ` · ${t.primary_assignee_name}` : ''}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex justify-between items-center gap-2 px-6 py-3 border-t border-gray-100">
              <span className="gt-page-sub" style={{ fontSize: 12 }}>
                {fromLabel ? `From ${fromLabel}` : 'From their current projects'}
              </span>
              <div className="flex gap-2">
                <button onClick={onClose} className="gt-btn is-ghost">Cancel</button>
                <button
                  onClick={goToProjects}
                  disabled={selectedIds.size === 0}
                  className="gt-btn is-primary"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {/* ---------------- STEP 2 — pick the project ---------------- */}
        {step === 2 && (
          <>
            <div className="px-6 pt-4 pb-2 space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={e => setProjectSearch(e.target.value)}
                  placeholder="Search projects…"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-2 min-h-[160px]">
              {loadingProjects ? (
                <div className="flex items-center justify-center gap-2 gt-page-sub py-10" style={{ fontSize: 13 }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
                </div>
              ) : visibleProjects.length === 0 ? (
                <p className="gt-page-sub text-center py-10" style={{ fontSize: 13 }}>
                  {projects.length === 0
                    ? 'No other active project to deliver into. Create one first.'
                    : `No project matches “${projectSearch}”.`}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {visibleProjects.map(p => (
                    <li key={p.id}>
                      <label className="flex items-center gap-3 py-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="deliver-target-project"
                          checked={targetId === p.id}
                          onChange={() => setTargetId(p.id)}
                          className="w-4 h-4 cursor-pointer flex-shrink-0"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-gray-900">
                            {p.name}
                            {p.code ? (
                              <span className="gt-numerals text-gray-400 font-normal"> ({p.code})</span>
                            ) : null}
                          </span>
                          <span className="gt-page-sub block mt-0.5" style={{ fontSize: 12 }}>
                            {p.status}
                            {typeof p.task_counts?.total === 'number'
                              ? ` · ${p.task_counts.total} task${p.task_counts.total === 1 ? '' : 's'}`
                              : ''}
                          </span>
                        </span>
                        {targetId === p.id && <Check className="w-4 h-4 text-green-600 flex-shrink-0" />}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="px-6 pb-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Note <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="e.g. handed over after phase 1"
              />
            </div>

            <div className="flex justify-between items-center gap-2 px-6 py-3 border-t border-gray-100">
              <button
                onClick={() => setStep(1)}
                disabled={delivering}
                className="gt-btn is-ghost"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-2">
                <span className="gt-page-sub gt-numerals" style={{ fontSize: 12 }}>
                  {selectedIds.size} task{selectedIds.size === 1 ? '' : 's'}
                  {targetProject ? ` → ${targetProject.name}` : ''}
                </span>
                <button
                  onClick={doDeliver}
                  disabled={delivering || !targetId || selectedIds.size === 0}
                  className="gt-btn is-primary"
                >
                  {delivering
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Delivering…</>
                    : <><FolderInput className="w-4 h-4" /> Deliver</>}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
